import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Search, Eye, Mail, Phone, Calendar, Shield,
  User as UserIcon, Trash2
} from 'lucide-react';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // User detail dialog
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userBookings, setUserBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  
  // Role change
  const [changingRole, setChangingRole] = useState(false);
  const [newRole, setNewRole] = useState('');
  
  // Delete user
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Fetch users error:', error);
      toast.error('Kullanıcılar yüklenirken hata oluştu');
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    // Search
    if (searchTerm) {
      filtered = filtered.filter(u => 
        u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.phone?.includes(searchTerm)
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(u => u.role === roleFilter);
    }

    setFilteredUsers(filtered);
  };

  const handleViewDetails = async (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setShowDetailDialog(true);
    
    // Fetch user bookings
    setLoadingBookings(true);
    try {
      const response = await axios.get(`${API}/admin/bookings`, { withCredentials: true });
      const userBookings = response.data.filter(b => b.user_id === user.user_id);
      setUserBookings(userBookings);
    } catch (error) {
      console.error('Fetch bookings error:', error);
    } finally {
      setLoadingBookings(false);
    }
  };

  const handleRoleChange = async () => {
    if (!selectedUser || newRole === selectedUser.role) return;
    
    setChangingRole(true);
    try {
      await axios.post(
        `${API}/admin/users/${selectedUser.user_id}/role`,
        { role: newRole },
        { withCredentials: true }
      );
      toast.success('Kullanıcı rolü güncellendi');
      setShowDetailDialog(false);
      fetchUsers();
    } catch (error) {
      console.error('Role change error:', error);
      toast.error(error.response?.data?.detail || 'Rol değiştirme başarısız');
    } finally {
      setChangingRole(false);
    }
  };

  const handleDeleteUser = (user) => {
    setDeletingUser(user);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(
        `${API}/admin/users/${deletingUser.user_id}`,
        { withCredentials: true }
      );
      toast.success('Kullanıcı silindi');
      setShowDeleteDialog(false);
      fetchUsers();
    } catch (error) {
      console.error('Delete user error:', error);
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    } finally {
      setDeleting(false);
    }
  };

  const getRoleBadge = (role) => {
    const config = {
      admin: { label: 'Admin', color: 'bg-red-100 text-red-700' },
      operator: { label: 'Operatör', color: 'bg-blue-100 text-blue-700' },
      user: { label: 'Kullanıcı', color: 'bg-slate-100 text-slate-700' }
    };
    const c = config[role] || config.user;
    return <Badge className={c.color}>{c.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const config = {
      CONFIRMED: { label: 'Onaylandı', color: 'bg-green-100 text-green-700' },
      PENDING_PAYMENT: { label: 'Bekliyor', color: 'bg-orange-100 text-orange-700' },
      CANCELLED: { label: 'İptal', color: 'bg-red-100 text-red-700' },
      EXPIRED: { label: 'Doldu', color: 'bg-slate-100 text-slate-700' }
    };
    const c = config[status] || config.PENDING_PAYMENT;
    return <span className={`px-2 py-1 rounded-full text-xs ${c.color}`}>{c.label}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const confirmedBookings = userBookings.filter(b => b.payment_status === 'CONFIRMED').length;
  const totalSpent = userBookings
    .filter(b => b.payment_status === 'CONFIRMED')
    .reduce((sum, b) => sum + (b.brut_tutar || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Müşteriler</h1>
          <p className="text-sm text-slate-600 mt-1">
            Toplam {users.length} kullanıcı • Görüntülenen {filteredUsers.length}
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Search */}
            <div className="relative md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Ad, email veya telefon ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Role Filter */}
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm Roller</SelectItem>
                <SelectItem value="user">Kullanıcı</SelectItem>
                <SelectItem value="operator">Operatör</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ad Soyad</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Telefon</TableHead>
                <TableHead>Rol</TableHead>
                <TableHead>Kayıt Tarihi</TableHead>
                <TableHead className="text-right">İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    Kullanıcı bulunamadı
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.user_id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-semibold">
                          {user.name?.charAt(0) || 'U'}
                        </div>
                        {user.name}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Mail className="w-3 h-3" />
                        {user.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      {user.phone ? (
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Phone className="w-3 h-3" />
                          {user.phone}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>{getRoleBadge(user.role)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Calendar className="w-3 h-3" />
                        {user.created_at ? format(new Date(user.created_at), 'd MMM yyyy', { locale: tr }) : '-'}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetails(user)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Detay
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteUser(user)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Sil
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* User Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Kullanıcı Detayları</DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-6 py-4">
              {/* User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-600">Ad Soyad</p>
                  <p className="font-medium">{selectedUser.name}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Email</p>
                  <p className="font-medium">{selectedUser.email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Telefon</p>
                  <p className="font-medium">{selectedUser.phone || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600">Kayıt Tarihi</p>
                  <p className="font-medium">
                    {selectedUser.created_at ? format(new Date(selectedUser.created_at), 'd MMMM yyyy', { locale: tr }) : '-'}
                  </p>
                </div>
              </div>

              {/* Role Change */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-2">Rol Değiştir</p>
                <div className="flex items-center gap-3">
                  <Select value={newRole} onValueChange={setNewRole}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">Kullanıcı</SelectItem>
                      <SelectItem value="operator">Operatör</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleRoleChange}
                    disabled={changingRole || newRole === selectedUser.role}
                    size="sm"
                  >
                    {changingRole ? 'Güncelleniyor...' : 'Güncelle'}
                  </Button>
                </div>
              </div>

              {/* Booking Stats */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Rezervasyon İstatistikleri</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-slate-900">{userBookings.length}</p>
                    <p className="text-xs text-slate-600">Toplam Rezervasyon</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-700">{confirmedBookings}</p>
                    <p className="text-xs text-green-600">Onaylı</p>
                  </div>
                  <div className="text-center p-3 bg-emerald-50 rounded-lg">
                    <p className="text-2xl font-bold text-emerald-700">{totalSpent.toLocaleString('tr-TR')} ₺</p>
                    <p className="text-xs text-emerald-600">Toplam Harcama</p>
                  </div>
                </div>
              </div>

              {/* Recent Bookings */}
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Son Rezervasyonlar</p>
                {loadingBookings ? (
                  <p className="text-center text-slate-500 py-4">Yükleniyor...</p>
                ) : userBookings.length === 0 ? (
                  <p className="text-center text-slate-500 py-4">Rezervasyon yok</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {userBookings.slice(0, 5).map(booking => (
                      <div key={booking.booking_id} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{booking.facility_name}</p>
                          <p className="text-xs text-slate-600">
                            {booking.date} • {booking.start_time}-{booking.end_time}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <p className="font-semibold text-sm">{booking.brut_tutar} ₺</p>
                          {getStatusBadge(booking.payment_status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kullanıcı Sil</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              <strong>{deletingUser?.name}</strong> kullanıcısını silmek istediğinize emin misiniz?
            </p>
            <p className="text-sm text-red-600 mt-2">
              ⚠️ Bu işlem geri alınamaz. Kullanıcının tüm rezervasyonları ve verileri silinecektir.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              İptal
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? 'Siliniyor...' : 'Evet, Sil'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
